import React, { useState, useEffect, useRef } from 'react';
import Calendar from 'react-calendar';
import { useMediaQuery } from 'react-responsive';
import PropTypes from 'prop-types';

import calendarIcon from './calendar.svg';
import arrowRight from './arrow-right.svg';
import arrowLeft from './arrow-left.svg';
import moment from 'moment';

const defaultProps = {
  startDate: null,
  endDate: null,
  maxBookingRange: 17,
  useMoment: false,
  allowOneSingleDaySelect: false,
  startDateId: 'start_date',
  endDateId: 'end_date',
};

const DatePicker = (properties) => {
  const props = {
    ...defaultProps,
    ...properties,
  };
  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 920px)' });
  const today = new Date();
  const calendarDateFormatOptions = {
    weekday: 'short',
  };
  const dateFormats = {
    fi: 'fi-FI',
    en: 'en-US',
  };

  const dateFormatLanguage = dateFormats[drupalSettings.current_language];

  const wrapperRef = useRef(null);
  closeCalendar(wrapperRef);

  function getNextMonth(baseDate) {
    const date = new Date(baseDate);
    // Set date as first of the month to avoid 
    // overflowing on months with days < 31.
    date.setDate(1);
    date.setMonth(date.getMonth() + 1);
    return date;
  }

  function getPrevMonth(baseDate) {
    const date = new Date(baseDate);
    date.setDate(1);
    date.setMonth(date.getMonth() - 1);
    return date;
  }

  const [showCalendar, setShowCalendar] = useState(false);
  const [dates, setDates] = useState([]);
  const [activeStartDate, setActiveStartDate] = useState(() => {
    const today = new Date();
    today.setDate(1);
    return today;
  });
  const [nextMonth, setNextMonth] = useState(() => {
    const date = new Date();
    date.setDate(1);
    date.setMonth(date.getMonth() + 1);
    return date;
  });

  // These control how far in the future or how far back the user
  // can select dates.
  const [minDate, setMinDate] = useState();
  const [maxDate, setMaxDate] = useState();

  // Close calendar when clicked outside of the element.
  function closeCalendar(ref) {
    useEffect(() => {
      function handleClickOutside(event) {
        if (ref.current && !ref.current.contains(event.target)) {
          setShowCalendar(false);
        }
      }
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [ref]);
  }

  function toggleCalendar() {
    if (props.disabled) {
      return;
    }
    setShowCalendar(!showCalendar);
  }

  function onDatesChange(value) {
    setDates(value);

    if (!props.useMoment) {
      props.setStartDate(value[0]);
      props.setEndDate(value[1]);
    } else {
      props.setStartDate(moment(value[0]));
      props.setEndDate(moment(value[1]));
    }

    setShowCalendar(false);
  }

  function resetCalendar(e) {
    e.preventDefault();
    setDates([]);
    props.setStartDate(null);
    props.setEndDate(null);
    setMinDate(null);
    setMaxDate(null);
  }

  function incrementMonth(e) {
    e.preventDefault();
    const newDate = getNextMonth(activeStartDate);
    setActiveStartDate(newDate);
    setNextMonth(getNextMonth(newDate));
  }

  function decrementMonth(e) {
    e.preventDefault();
    const newDate = getPrevMonth(activeStartDate);
    setActiveStartDate(newDate);
    setNextMonth(getNextMonth(newDate));
  }

  function tileClassName({ date, view }) {
    if (view === 'month') {
      if (date < today && date.toDateString() !== today.toDateString()) {
        return 'react-calendar__month-view__days__day--past';
      } else {
        if (props.parseDay) {
          return props.parseDay(date);
        }
      }
    }
  }

  function formatDate(date) {
    const offset = date.getTimezoneOffset();
    const formattedDate = new Date(date.getTime() - offset * 60 * 1000);
    return formattedDate.toISOString().split('T')[0];
  }

  // Prevent the selected date range from including
  // blocked dates.
  function updateRange(date) {
    setMinDate(date);
    if (!props.useMoment) {
      props.setStartDate(date);
    } else {
      props.setStartDate(moment(date));
    }

    let selectedDate = new Date(date);
    for (let i = 0; i < props.maxBookingRange; i++) {
      const tomorrow = new Date(selectedDate);
      selectedDate = new Date(
        tomorrow.setUTCDate(selectedDate.getUTCDate() + 1)
      );

      // Set max date if a user can select a blocked date.
      const dateString = formatDate(selectedDate);
      if (props.availableDates && !props.availableDates[dateString]) {
        setMaxDate(selectedDate);
        break;
      }

      if (props.dayIsDisabled && props.dayIsDisabled(selectedDate)) {
        setMaxDate(selectedDate);
        break;
      }
    }
  }

  function disableDay({ date, view }) {
    if (view === 'month') {
      // If the day is before today.
      if (date < today && date.toDateString() !== today.toDateString()) {
        return true;
      } else if (props.startDate && !props.endDate) {
        let start = props.startDate;
        if (props.useMoment) {
          start = new Date(props.startDate.toISOString());
        }
        // Prevent user from selecting the same day for start and
        // end dates.
        if (
          !props.allowOneSingleDaySelect &&
          date.toDateString() === start.toDateString()
        ) {
          return true;
        }
      }

      if (props.dayIsDisabled) {
        return props.dayIsDisabled(date);
      }
    }
  }

  // If user picked only the start date but then closed
  // the calendar, reset min and max date values.
  useEffect(() => {
    if (!showCalendar) {
      if (!props.endDate) {
        setMinDate(null);
        setMaxDate(null);
        props.setStartDate(null);
      }
    }
  }, [showCalendar]);

  // Set the initial value if provided.
  useEffect(() => {
    if (dates && dates.length > 0) {
      return;
    }

    if (!props.startDate || !props.endDate) {
      return;
    }

    if (!props.useMoment) {
      setDates([props.startDate], props.endDate);
    } else {
      setDates([
        props.startDate.format('YYYY-MM-DD'),
        props.endDate.format('YYYY-MM-DD'),
      ]);
    }
  }, []);

  return (
    <div className="datepicker-wrapper">
      <div
        className={`datepicker ${props.disabled ? 'datepicker__disabled' : ''}`}
      >
        <img onClick={toggleCalendar} src={calendarIcon} alt="Calendar" />

        {!dates || (dates.length === 0 && props.disabled) ? (
          <p>{Drupal.t('Loading...')}</p>
        ) : (
          <div>
            <input
              type="text"
              onClick={toggleCalendar}
              id={props.startDateId}
              className="start-date"
              readOnly={true}
              value={
                props.startDate
                  ? !props.useMoment
                    ? props.startDate.toLocaleDateString('fi-FI')
                    : props.startDate.format('DD.MM.YYYY')
                  : Drupal.t('Check in')
              }
            />

            <img src={arrowRight} />
            <input
              type="text"
              onClick={toggleCalendar}
              id={props.endDateId}
              className="start-date"
              readOnly={true}
              value={
                props.endDate
                  ? !props.useMoment
                    ? props.endDate.toLocaleDateString('fi-FI')
                    : props.endDate.format('DD.MM.YYYY')
                  : Drupal.t('Check out')
              }
            />
          </div>
        )}
        {(props.startDate || props.endDate) && (
          <button className="datepicker__clear-date" onClick={resetCalendar}>
            ✕
          </button>
        )}
      </div>

      {showCalendar && (
        <div ref={wrapperRef} className="react-calendar__calendar">
          <div className="react-calendar__calendar--controls">
            <div className="arrow-container">
              <button
                className="arrow-container__arrow arrow-container__arrow--prev"
                onClick={decrementMonth}
              >
                <img
                  src={arrowLeft}
                  alt={Drupal.t('Previous month') ?? 'Previous month'}
                />
              </button>
              {!isTabletOrMobile && (
                <p className="arrow-container__month-name">
                  {activeStartDate.toLocaleDateString(dateFormatLanguage, {
                    month: 'long',
                  })}{' '}
                  {activeStartDate.getFullYear()}
                </p>
              )}
            </div>
            {isTabletOrMobile && (
              <p className="arrow-container__month-name">
                {activeStartDate.toLocaleDateString(dateFormatLanguage, {
                  month: 'long',
                })}{' '}
                {activeStartDate.getFullYear()}
              </p>
            )}
            <div className="arrow-container">
              {!isTabletOrMobile && (
                <p className="arrow-container__month-name">
                  {nextMonth.toLocaleDateString(dateFormatLanguage, {
                    month: 'long',
                  })}{' '}
                  {nextMonth.getFullYear()}
                </p>
              )}

              <button
                className="arrow-container__arrow arrow-container__arrow--next"
                onClick={incrementMonth}
              >
                <img
                  src={arrowRight}
                  alt={Drupal.t('Next month') ?? 'Next month'}
                />
              </button>
            </div>
          </div>
          <Calendar
            selectRange={true}
            minDate={minDate}
            maxDate={maxDate}
            value={dates}
            onClickDay={(e) => updateRange(e)}
            onChange={onDatesChange}
            tileClassName={tileClassName}
            returnValue="range"
            showDoubleView={!isTabletOrMobile}
            tileDisabled={disableDay}
            showWeekNumbers={true}
            formatShortWeekday={(locale, date) =>
              date.toLocaleDateString(
                dateFormatLanguage,
                calendarDateFormatOptions
              )
            }
            activeStartDate={activeStartDate}
            showNavigation={false}
            view={'month'}
          />

          {props.content && props.content}
        </div>
      )}
    </div>
  );
};

DatePicker.propTypes = {
  setStartDate: PropTypes.func,
  setEndDate: PropTypes.func,
  startDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(moment),
  ]),
  endDate: PropTypes.oneOfType([
    PropTypes.instanceOf(Date),
    PropTypes.instanceOf(moment),
  ]),
  parseDay: PropTypes.func,
  dayIsDisabled: PropTypes.func,
  disabled: PropTypes.bool,
  availableDates: PropTypes.object,
  maxBookingRange: PropTypes.number,
  content: PropTypes.element,
  useMoment: PropTypes.bool,
  allowOneSingleDaySelect: PropTypes.bool,
  startDateId: PropTypes.string,
  endDateId: PropTypes.string,
};

export default DatePicker;
